import React from 'react';
import { Link } from 'react-router-dom';
import { NotificationItem } from './NotificationItem';
import { useNotifications } from '@/hooks/useNotifications';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { CheckCheck, Inbox } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { listItem, revealStagger } from '@/lib/motion/presets';

interface NotificationDropdownProps {
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
  const { notifications, markAllAsRead, isLoading } = useNotifications();
  const recentNotifications = notifications.slice(0, 10);
  const reducedMotion = useReducedMotion();

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <h3 className="font-semibold text-lg">Notifications</h3>
        {recentNotifications.some(n => !n.read) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAllAsRead()}
            className="text-xs"
          >
            <CheckCheck className="h-3 w-3 mr-1" />
            Mark all read
          </Button>
        )}
      </div>

      <Separator />

      {/* Notifications List */}
      <ScrollArea className="h-[400px]">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : recentNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Inbox className="h-12 w-12 mb-3 opacity-50" />
            <p className="text-sm">No notifications yet</p>
            <p className="text-xs mt-1">We'll notify you when something happens</p>
          </div>
        ) : (
          <motion.div
            className="divide-y"
            initial={reducedMotion ? false : 'hidden'}
            animate="show"
            variants={revealStagger}
          >
            {recentNotifications.map((notification) => (
              <motion.div key={notification.id} variants={listItem}>
                <NotificationItem notification={notification} onClose={onClose} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </ScrollArea>

      {/* Footer */}
      {recentNotifications.length > 0 && (
        <>
          <Separator />
          <div className="p-3">
            <Link to="/notifications" onClick={onClose}>
              <Button variant="ghost" className="w-full text-sm">
                See all notifications
              </Button>
            </Link>
          </div>
        </>
      )}
    </div>
  );
};
